import React, {createElement, memo, useCallback, useEffect, useRef, useState} from 'react'

import axios from 'axios'

import {posthogTrack} from 'src/utils'
import {loadScript} from 'src/utils/loadScript'

type TApplePayButtonProps = {
	price: number
	onSubmit: ({
		alternativePaymentToken,
		alternativePaymentType,
	}: {
		alternativePaymentToken: Sticky.TAlternativePaymentToken
		alternativePaymentType: Sticky.TAlternativePaymentType
	}) => Promise<void>
}

let session: ApplePaySession | null = null

const ApplePayButton = memo(({price, onSubmit}: TApplePayButtonProps) => {
	const [hasError, setHasError] = useState(false)
	const buttonRef = useRef<HTMLButtonElement | null>(null)

	const initialize = useCallback(async () => {
		await loadScript('https://applepay.cdn-apple.com/jsapi/v1.1.0/apple-pay-sdk.js')

		if (typeof ApplePaySession === 'undefined' || !ApplePaySession.canMakePayments()) {
			return
		}

		// Set up your Apple Pay button here
		buttonRef.current?.addEventListener('click', (event) => {
			event.preventDefault()
			if (session) return

			setHasError(false)
			posthogTrack('subscription/btn_clicked', {type: 'apple pay', gateway: 'checkoutltd'})

			// Create ApplePaySession
			session = new ApplePaySession(3, {
				countryCode: 'US',
				currencyCode: 'USD',
				merchantCapabilities: ['supports3DS'],
				supportedNetworks: ['visa', 'masterCard', 'amex', 'discover', 'jcb'],
				total: {
					label: 'paystub.org', // the name of the store to be displayed to the user
					amount: String(price),
				},
			})

			session.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
				// Call your backend endpoint to request a new merchant session.
				try {
					const merchantSession = await axios.post('/applepay', {url: event.validationURL})
					session?.completeMerchantValidation(merchantSession.data)
				} catch (err) {
					console.error('Error validating merchant', err)
					session?.abort()
					setHasError(true)
				}
			}

			session.onpaymentauthorized = async (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
				// Token must be passed as "wallet_token" to Sticky's "/v1/new_order"

				await onSubmit({
					alternativePaymentToken: event.payment.token,
					alternativePaymentType: 'applepay',
				})

				session?.completePayment({
					status: ApplePaySession.STATUS_SUCCESS,
				})
			}

			session.oncancel = () => {
				session = null
			}

			session.begin()
		})
	}, [onSubmit, price])

	useEffect(() => {
		void initialize()
	})

	if (typeof window === 'undefined') return null
	if (typeof ApplePaySession === 'undefined') return null

	return (
		<>
			<div
				style={{
					width: '100%',
					height: '40px',
					overflow: 'hidden',
					marginTop: '10px',
				}}>
				{createElement('apple-pay-button', {
					type: 'subscribe',
					buttonstyle: 'black',
					ref: buttonRef,
					style: {
						'--apple-pay-button-box-sizing': 'border-box',
						'--apple-pay-button-width': '100%',
						'--apple-pay-button-height': '40px',
						'--apple-pay-button-padding': '4px',
					},
				})}
			</div>
			{hasError && (
				<div className="fadeIn" style={{color: 'var(--red400)', padding: '10px 0'}}>
					Something went wrong while processing payment.
					<br /> Please try again later.
				</div>
			)}
		</>
	)
})

ApplePayButton.displayName = 'ApplePayButton'

export default ApplePayButton
